// src/data/languages/swahili/sentences.ts

interface Sentence {
  original: string;
  french: string;
  english?: string;
  words: string[];
  audio?: string;
  category: string;
  tags: string[];
}

export const SENTENCE_CATEGORIES = {
  'Salutations': {
    icon: '👋',
    description: 'Premiers contacts et formules de politesse',
  },
  'Présentation': {
    icon: '🤝',
    description: 'Se présenter et faire connaissance',
  },
  'Vie quotidienne': {
    icon: '🌞',
    description: 'Expressions du quotidien',
  },
  'Au marché': {
    icon: '🛍️',
    description: 'Faire ses courses et négocier',
  },
  'Voyages': {
    icon: '✈️',
    description: 'Se déplacer et visiter',
  },
  'Famille et relations': {
    icon: '👨‍👩‍👧‍👦',
    description: 'Parler de sa famille et de ses proches',
  },
  'Urgences et santé': {
    icon: '🚨',
    description: 'Expressions pour les situations médicales et urgentes',
  },
  'Loisirs et culture': {
    icon: '🎭',
    description: 'Parler de ses activités, passe-temps et culture',
  },
} as const;

export const sentencesToConstruct: Sentence[] = [
  // ===== SALUTATIONS =====
  {
    original: "Habari za asubuhi",
    french: "Bonjour (nouvelles du matin)",
    english: "Good morning (morning news)",
    words: ["Habari", "za", "asubuhi"],
    audio: "Sw-Habari_za_asubuhi",
    category: 'Salutations',
    tags: ['salutations', 'matin']
  },
  {
    original: "Nzuri za asubuhi",
    french: "Bien du matin (réponse au bonjour)",
    english: "Fine this morning (reply to good morning)",
    words: ["Nzuri", "za", "asubuhi"],
    category: 'Salutations',
    tags: ['salutations', 'matin', 'réponse']
  },
  {
    original: "Habari za mchana",
    french: "Bon après-midi",
    english: "Good afternoon",
    words: ["Habari", "za", "mchana"],
    audio: "Sw-Habari_za_mchana",
    category: 'Salutations',
    tags: ['salutations', 'après-midi']
  },
  {
    original: "Habari za jioni",
    french: "Bonsoir",
    english: "Good evening",
    words: ["Habari", "za", "jioni"],
    audio: "Sw-Habari_za_jioni",
    category: 'Salutations',
    tags: ['salutations', 'soir']
  },
  {
    original: "Amani tu",
    french: "Paix seulement",
    english: "Peace only",
    words: ["Amani", "tu"],
    category: 'Salutations',
    tags: ['salutations', 'paix']
  },

  // ===== PRÉSENTATION =====
  {
    original: "Jina langu ni",
    french: "Mon nom est / Je m'appelle",
    english: "My name is / I am called",
    words: ["Jina", "langu", "ni"],
    audio: "Sw-Jina_langu_ni",
    category: 'Présentation',
    tags: ['nom', 'présentation']
  },
  {
    original: "Ninaitwa",
    french: "Je m'appelle",
    english: "I am called",
    words: ["Ninaitwa"],
    category: 'Présentation',
    tags: ['nom', 'présentation']
  },
  {
    original: "Jina lako nani",
    french: "Comment tu t'appelles ?",
    english: "What is your name?",
    words: ["Jina", "lako", "nani"],
    category: 'Présentation',
    tags: ['présentation', 'question']
  },
  {
    original: "Nina jina",
    french: "J'ai un nom",
    english: "I have a name",
    words: ["Nina", "jina"],
    category: 'Présentation',
    tags: ['nom', 'identité']
  },
  {
    original: "Ninatoka",
    french: "Je viens de",
    english: "I come from",
    words: ["Ninatoka"],
    audio: "Sw-Ninatoka",
    category: 'Présentation',
    tags: ['origine', 'lieu']
  },
  {
    original: "Unatoka wapi",
    french: "Tu viens d'où ?",
    english: "Where do you come from?",
    words: ["Unatoka", "wapi"],
    audio: "Sw-Unatoka_wapi",
    category: 'Présentation',
    tags: ['origine', 'question']
  },
  {
    original: "Nina miaka kumi",
    french: "J'ai dix ans",
    english: "I am ten years old",
    words: ["Nina", "miaka", "kumi"],
    audio: "Sw-Nina_miaka_kumi",
    category: 'Présentation',
    tags: ['âge', 'description']
  },

  // ===== VIE QUOTIDIENNE =====
  {
    original: "Nimechoka",
    french: "Je suis fatigué",
    english: "I am tired",
    words: ["Nimechoka"],
    audio: "Sw-Nimechoka",
    category: 'Vie quotidienne',
    tags: ['état', 'fatigue']
  },
  {
    original: "Ninakula",
    french: "Je mange",
    english: "I am eating",
    words: ["Ninakula"],
    category: 'Vie quotidienne',
    tags: ['nourriture', 'activité']
  },
  {
    original: "Nina kiu",
    french: "J'ai soif",
    english: "I am thirsty",
    words: ["Nina", "kiu"],
    audio: "Sw-Nina_kiu",
    category: 'Vie quotidienne',
    tags: ['boisson', 'état']
  },
  {
    original: "Saa ngapi",
    french: "Quelle heure est-il ?",
    english: "What time is it?",
    words: ["Saa", "ngapi"],
    category: 'Vie quotidienne',
    tags: ['temps', 'question']
  },
  {
    original: "Ni wakati gani",
    french: "C'est quelle heure ?",
    english: "What time is it?",
    words: ["Ni", "wakati", "gani"],
    category: 'Vie quotidienne',
    tags: ['temps', 'question']
  },
  {
    original: "Ninaenda",
    french: "Je pars / Je vais",
    english: "I am leaving / I am going",
    words: ["Ninaenda"],
    category: 'Vie quotidienne',
    tags: ['déplacement', 'action']
  },
  {
    original: "Ninalala",
    french: "Je dors",
    english: "I am sleeping",
    words: ["Ninalala"],
    category: 'Vie quotidienne',
    tags: ['repos', 'activité']
  },

  // ===== AU MARCHÉ =====
  {
    original: "Bei ngapi",
    french: "Combien ça coûte ?",
    english: "How much does it cost?",
    words: ["Bei", "ngapi"],
    category: 'Au marché',
    tags: ['prix', 'question']
  },
  {
    original: "Ni gharama gani",
    french: "C'est quel prix ?",
    english: "What is the price?",
    words: ["Ni", "gharama", "gani"],
    category: 'Au marché',
    tags: ['prix', 'question']
  },
  {
    original: "Ninataka",
    french: "Je cherche / Je veux",
    english: "I am looking for / I want",
    words: ["Ninataka"],
    category: 'Au marché',
    tags: ['achat', 'recherche']
  },
  {
    original: "Ni ghali sana",
    french: "C'est trop cher",
    english: "It's too expensive",
    words: ["Ni", "ghali", "sana"],
    audio: "Sw-Ni_ghali_sana",
    category: 'Au marché',
    tags: ['prix', 'négociation']
  },
  {
    original: "Nataka kununua",
    french: "Je veux acheter",
    english: "I want to buy",
    words: ["Nataka", "kununua"],
    category: 'Au marché',
    tags: ['achat', 'intention']
  },
  {
    original: "Hakuna",
    french: "Il n'y en a pas",
    english: "There isn't any",
    words: ["Hakuna"],
    category: 'Au marché',
    tags: ['disponibilité', 'négation']
  },

  // ===== VOYAGES =====
  {
    original: "Ninaenda",
    french: "Je vais à",
    english: "I am going to",
    words: ["Ninaenda"],
    category: 'Voyages',
    tags: ['déplacement', 'destination']
  },
  {
    original: "Teksi iko wapi",
    french: "Où est le taxi ?",
    english: "Where is the taxi?",
    words: ["Teksi", "iko", "wapi"],
    category: 'Voyages',
    tags: ['transport', 'lieu']
  },
  {
    original: "Dereva yu wapi",
    french: "Où est le chauffeur ?",
    english: "Where is the driver?",
    words: ["Dereva", "yu", "wapi"],
    category: 'Voyages',
    tags: ['transport', 'personne']
  },
  {
    original: "Nataka tiketi",
    french: "Je veux un billet",
    english: "I want a ticket",
    words: ["Nataka", "tiketi"],
    category: 'Voyages',
    tags: ['transport', 'document']
  },
  {
    original: "Ni mbali",
    french: "C'est loin",
    english: "It's far",
    words: ["Ni", "mbali"],
    category: 'Voyages',
    tags: ['distance', 'description']
  },

  // ===== FAMILLE ET RELATIONS =====
  {
    original: "Mama",
    french: "Mère",
    english: "Mother",
    words: ["Mama"],
    audio: "Sw-Mama",
    category: 'Famille et relations',
    tags: ['famille', 'parent']
  },
  {
    original: "Baba",
    french: "Père",
    english: "Father",
    words: ["Baba"],
    audio: "Sw-Baba",
    category: 'Famille et relations',
    tags: ['famille', 'parent']
  },
  {
    original: "Mume wangu",
    french: "Mon mari",
    english: "My husband",
    words: ["Mume", "wangu"],
    category: 'Famille et relations',
    tags: ['famille', 'conjoint']
  },
  {
    original: "Mke wangu",
    french: "Ma femme",
    english: "My wife",
    words: ["Mke", "wangu"],
    category: 'Famille et relations',
    tags: ['famille', 'conjoint']
  },
  {
    original: "Nina watoto",
    french: "J'ai des enfants",
    english: "I have children",
    words: ["Nina", "watoto"],
    category: 'Famille et relations',
    tags: ['famille', 'enfant']
  },
  {
    original: "Nyawira wangu",
    french: "Ma grand-mère",
    english: "My grandmother",
    words: ["Nyawira", "wangu"],
    category: 'Famille et relations',
    tags: ['famille', 'grand-parent']
  },

  // ===== URGENCES ET SANTÉ =====
  {
    original: "Msaada",
    french: "Au secours / À l'aide",
    english: "Help! / Assistance",
    words: ["Msaada"],
    category: 'Urgences et santé',
    tags: ['urgence', 'aide']
  },
  {
    original: "Sihisi vizuri",
    french: "Je ne me sens pas bien",
    english: "I don't feel well",
    words: ["Sihisi", "vizuri"],
    audio: "Sw-Sihisi_vizuri",
    category: 'Urgences et santé',
    tags: ['urgence', 'santé']
  },
  {
    original: "Ninaumwa",
    french: "Je suis malade / J'ai mal",
    english: "I am sick / I am in pain",
    words: ["Ninaumwa"],
    category: 'Urgences et santé',
    tags: ['santé', 'douleur']
  },
  {
    original: "Nataka daktari",
    french: "Je veux un docteur",
    english: "I want a doctor",
    words: ["Nataka", "daktari"],
    category: 'Urgences et santé',
    tags: ['santé', 'médecin']
  },
  {
    original: "Hospitali iko wapi",
    french: "Où est l'hôpital ?",
    english: "Where is the hospital?",
    words: ["Hospitali", "iko", "wapi"],
    category: 'Urgences et santé',
    tags: ['santé', 'lieu', 'urgence']
  },
  {
    original: "Ninaumwa kichwa",
    french: "J'ai mal à la tête",
    english: "I have a headache",
    words: ["Ninaumwa", "kichwa"],
    category: 'Urgences et santé',
    tags: ['santé', 'douleur', 'tête']
  },
  {
    original: "Nataka dawa",
    french: "Je veux des médicaments",
    english: "I want some medicine",
    words: ["Nataka", "dawa"],
    category: 'Urgences et santé',
    tags: ['santé', 'médicament']
  },

  // ===== LOISIRS ET CULTURE =====
  {
    original: "Napenda muziki",
    french: "J'aime la musique",
    english: "I like music",
    words: ["Napenda", "muziki"],
    category: 'Loisirs et culture',
    tags: ['loisirs', 'musique', 'goûts']
  },
  {
    original: "Ninacheza mpira",
    french: "Je joue au football",
    english: "I am playing football",
    words: ["Ninacheza", "mpira"],
    category: 'Loisirs et culture',
    tags: ['sport', 'football', 'jeu']
  },
  {
    original: "Tuimbe pamoja",
    french: "Chantons ensemble",
    english: "Let's sing together",
    words: ["Tuimbe", "pamoja"],
    category: 'Loisirs et culture',
    tags: ['musique', 'social', 'chant']
  },
  {
    original: "Ninasoma kitabu",
    french: "Je lis un livre",
    english: "I am reading a book",
    words: ["Ninasoma", "kitabu"],
    category: 'Loisirs et culture',
    tags: ['lecture', 'livre', 'culture']
  },
  {
    original: "Twende sinemani",
    french: "Allons au cinéma",
    english: "Let's go to the cinema",
    words: ["Twende", "sinemani"],
    category: 'Loisirs et culture',
    tags: ['cinéma', 'sortie', 'culture']
  },
  {
    original: "Napenda mchezo",
    french: "J'aime les jeux",
    english: "I like games",
    words: ["Napenda", "mchezo"],
    category: 'Loisirs et culture',
    tags: ['jeu', 'loisirs', 'plaisir']
  }
];

// Fonctions utilitaires
export const getSentencesByCategory = (category: keyof typeof SENTENCE_CATEGORIES) =>
  sentencesToConstruct.filter(sentence => sentence.category === category);

export const getSentencesByTags = (tags: string[]) =>
  sentencesToConstruct.filter(sentence =>
    tags.some(tag => sentence.tags.includes(tag))
  );

export const getCategories = () => SENTENCE_CATEGORIES;

export default sentencesToConstruct;
